import type { ExhibitDocument } from "@upclaim/sdk/src/data-contracts";

/**
 * Exhibit number extracted from filename — port of utils/extract-exhibit-number.ts (Vue).
 * Examples: "Pièce n°7" → { main: 7, sub: null }, "Pièce n°7.1" → { main: 7, sub: 1 }
 */
export interface ExhibitNumber {
  main: number;
  sub: number | null;
}

export interface DocumentWithExhibit {
  id: string;
  file: { id: string; name: string; boxFileId?: string | null } | null;
  exhibit: ExhibitNumber | null;
  willBeStamped: boolean;
}

export interface ExhibitData {
  grouped: Map<number, DocumentWithExhibit[]>;
  withoutExhibits: DocumentWithExhibit[];
  toStamp: DocumentWithExhibit[];
  missingNumbers: number[];
}

/**
 * Extract exhibit number from filename
 * Supports: "Pièce n°7", "Pièce n°7.1", "pièce n° 12", "7", "7.1", etc.
 */
export function extractExhibitNumber(filename: string): ExhibitNumber | null {
  // Try "Pièce n°X" pattern first
  const pieceMatch = filename.match(/pi[eèé]ce.*?(\d+)(?:\.(\d+))?/i);
  if (pieceMatch) {
    return {
      main: Number.parseInt(pieceMatch[1], 10),
      sub: pieceMatch[2] ? Number.parseInt(pieceMatch[2], 10) : null,
    };
  }

  // Fallback: filename starts with 1-2 digit number (e.g., "1", "1.1", "12.3")
  // Only match 1-2 digits to avoid false positives like years (2024) or case numbers
  // Negative lookahead (?!\d) ensures no more digits follow (prevents "2024" matching as "20")
  const startsWithNumber = filename.match(/^(\d{1,2})(?:\.(\d+))?(?!\d)/);
  if (startsWithNumber) {
    return {
      main: Number.parseInt(startsWithNumber[1], 10),
      sub: startsWithNumber[2] ? Number.parseInt(startsWithNumber[2], 10) : null,
    };
  }

  return null;
}

/** Format exhibit number: { main: 7, sub: 1 } → "7.1" */
export function formatExhibitNumber(exhibit: ExhibitNumber): string {
  return exhibit.sub !== null ? `${exhibit.main}.${exhibit.sub}` : `${exhibit.main}`;
}

/** Sort each group in place: plain numbers first, then by sub-number. */
function sortGroup(docs: DocumentWithExhibit[]) {
  docs.sort((a, b) => {
    if (a.exhibit?.sub == null) {
      return -1;
    }
    if (b.exhibit?.sub == null) {
      return 1;
    }
    return a.exhibit.sub - b.exhibit.sub;
  });
}

/** Common tail of the grouping pipeline: sort groups, mark stamps, find gaps. */
function finalizeExhibitData(
  grouped: Map<number, DocumentWithExhibit[]>,
  withoutExhibits: DocumentWithExhibit[],
  preserveOrder = false,
): ExhibitData {
  if (!preserveOrder) {
    for (const docs of grouped.values()) {
      sortGroup(docs);
    }
  }

  // Sort the grouped Map by main number
  const sortedGrouped = preserveOrder
    ? grouped
    : new Map(Array.from(grouped.entries()).sort((a, b) => a[0] - b[0]));

  // Include all documents in toStamp, but only mark the first of each group as
  // willBeStamped (stamp icon in the UI); the backend receives them all.
  const toStamp: DocumentWithExhibit[] = [];
  for (const docs of sortedGrouped.values()) {
    docs[0].willBeStamped = true;
    for (const doc of docs) {
      toStamp.push(doc);
    }
  }

  // Find missing numbers (1 to max)
  const numbers = Array.from(sortedGrouped.keys());
  const max = Math.max(0, ...numbers);
  const missingNumbers = Array.from({ length: max }, (_, i) => i + 1).filter((n) => !numbers.includes(n));

  return { grouped: sortedGrouped, withoutExhibits, toStamp, missingNumbers };
}

/**
 * Group documents by exhibit number (extracted from the filename) and determine
 * which to stamp — port of groupAndPrepareExhibits (Vue).
 *
 * Stamping logic: stamp plain number (n°7) if it exists, else first sub-number (n°7.1).
 */
export function groupAndPrepareExhibits(
  documents: Array<{ id: string; file: { id: string; name: string; boxFileId?: string | null } | null }>,
): ExhibitData {
  const grouped = new Map<number, DocumentWithExhibit[]>();
  const withoutExhibits: DocumentWithExhibit[] = [];

  for (const doc of documents) {
    const exhibit = extractExhibitNumber(doc.file?.name || "");
    const docWithExhibit: DocumentWithExhibit = { ...doc, exhibit, willBeStamped: false };

    if (exhibit) {
      if (!grouped.has(exhibit.main)) {
        grouped.set(exhibit.main, []);
      }
      grouped.get(exhibit.main)?.push(docWithExhibit);
    } else {
      withoutExhibits.push(docWithExhibit);
    }
  }

  return finalizeExhibitData(grouped, withoutExhibits);
}

/**
 * Shape API exhibit documents (already carrying their exhibit numbers) into
 * ExhibitData — port of the useClaimExhibits.exhibitData computed (Vue).
 *
 * When `preserveOrder` is true the documents keep the caller's order (manual
 * reordering) instead of being re-sorted by exhibit number.
 */
export function groupExhibitDocuments(exhibits: ExhibitDocument[], preserveOrder = false): ExhibitData {
  const grouped = new Map<number, DocumentWithExhibit[]>();

  for (const doc of exhibits) {
    const docWithExhibit: DocumentWithExhibit = {
      id: doc.boxFileId,
      file: { id: doc.boxFileId, name: doc.name, boxFileId: doc.boxFileId },
      exhibit: { main: doc.exhibitNumber, sub: doc.exhibitSubNumber ?? null },
      willBeStamped: false,
    };

    if (!grouped.has(doc.exhibitNumber)) {
      grouped.set(doc.exhibitNumber, []);
    }
    grouped.get(doc.exhibitNumber)?.push(docWithExhibit);
  }

  return finalizeExhibitData(grouped, [], preserveOrder);
}

/** Sort exhibit documents by main number then sub-number (plain numbers first). */
export function sortExhibitDocuments(exhibits: ExhibitDocument[]): ExhibitDocument[] {
  return [...exhibits].sort((a, b) => {
    if (a.exhibitNumber !== b.exhibitNumber) {
      return a.exhibitNumber - b.exhibitNumber;
    }
    if (a.exhibitSubNumber == null) {
      return -1;
    }
    if (b.exhibitSubNumber == null) {
      return 1;
    }
    return a.exhibitSubNumber - b.exhibitSubNumber;
  });
}
